/**
 * Unit conversion + display helpers for weight (kg ↔ lbs ↔ stones).
 *
 * The DB stores weights canonically in kilograms (e.g. exercise.weight_kg,
 * estimated_weight_kg). This module is the single source of truth for
 * presenting and accepting weights in the user's preferred system.
 *
 * Why centralized: every workout surface that displays a weight (timer
 * rest input, WorkoutDetail chips, post-workout summary, expandable
 * exercise breakdowns, phase guidance, profile drawer, etc.) needs the
 * same rounding + label logic. Drift between callers leads to
 * "5.5 kg" vs "12.13 lbs" inconsistencies, and historically every new
 * surface re-invented the formatting and hardcoded "kg".
 *
 * 🚨 ALWAYS use formatWeight() (or the useFormatWeight() hook) when
 * rendering a weight in the UI. NEVER hardcode "kg" or "lbs" in JSX
 * text or template literals — the gate test
 * tests/gate/test_no_hardcoded_weight_units.py blocks regressions.
 *
 * Stones support (added 2026-05-20):
 * The DB schema in migration 1522 (`exercise_set_log.unit_logged`)
 * accepts {'kg', 'lbs', 'stones'}. ProfileContext historically typed
 * units as binary 'metric' | 'imperial', but PR #1406's Phase 4 (unit
 * sweep across native workout views) will surface stones in the UI.
 * This module's UnitSystem is extended to 'metric' | 'imperial' |
 * 'stones' so callers can pass the stones value through end-to-end.
 *
 * British display convention for stones: composite "X st Y lb"
 * (e.g. "12 st 7 lb" for ~80 kg). Single-number decimal-stones
 * (e.g. "12.5 st") loses the per-pound precision UK lifters expect.
 * formatWeight() emits the composite form for stones; kgToDisplay()
 * returns the decimal-stones single value for callers that compose
 * their own UI (e.g. two-field "stones / pounds" input). See
 * tests/gate/test_workout_units_lbs_kg.py for the contract.
 */

import { useMemo } from 'react';
import { useOptionalProfile, useProfile } from '../contexts/ProfileContext';
import { openWaterSwimMetric, resolveUnitPreferences, type ResolvedUnitPreferences } from './unitPreferences';
import { resolveDisplayRateUnit, type RateUnit } from './cardioUnits';

export type UnitSystem = 'metric' | 'imperial' | 'stones';

// SI conversion. 1 kg = 2.20462262185 lbs — truncating at 4 decimals
// gives precision well below typical plate increments (2.5 lb / 1.25 kg).
export const KG_TO_LBS = 2.2046;
export const LBS_TO_KG = 1 / KG_TO_LBS;

// 1 stone = 14 pounds (exact, by definition since 1959 international yard
// and pound agreement). Stones↔kg goes through lbs: kg → lbs → stones.
export const LBS_PER_STONE = 14;
export const KG_PER_STONE = LBS_PER_STONE / KG_TO_LBS; // ≈ 6.3503

/**
 * Display label for the user's preferred weight unit.
 *
 * Stones returns 'st' — the short label. Note: rendering "X st" alone
 * loses the customary pound remainder ("12 st 7 lb"). Callers that
 * combine `kgToDisplay() + weightUnitLabel()` for visible UI strings
 * should NOT use that split for stones — use `formatWeight()` instead,
 * which emits the composite form correctly.
 */
export function weightUnitLabel(units: UnitSystem | undefined): 'kg' | 'lbs' | 'st' {
  if (units === 'imperial') return 'lbs';
  if (units === 'stones') return 'st';
  return 'kg';
}

/**
 * Convert a kg value (DB storage) to the user's preferred display unit.
 *
 * Rounding:
 *   - metric: 0.5 kg (typical 1.25 kg plate)
 *   - imperial: 0.5 lbs (typical 2.5 lb plate)
 *   - stones: 0.1 stones (1.4 lbs — finer than the composite formatter,
 *     so callers building their own composite UI have headroom)
 *
 * NOTE: for stones, this returns decimal stones (e.g. 12.5). For the
 * customary British composite display "X st Y lb", use formatWeight()
 * instead.
 */
export function kgToDisplay(weightKg: number, units: UnitSystem | undefined): number {
  if (units === 'imperial') {
    return Math.round(weightKg * KG_TO_LBS * 2) / 2;
  }
  if (units === 'stones') {
    const stones = weightKg / KG_PER_STONE;
    return Math.round(stones * 10) / 10;
  }
  return Math.round(weightKg * 2) / 2;
}

/**
 * Convert a display-unit value (what the user typed) back to kg for
 * persistence. Identity for metric users.
 *
 * For stones: input is decimal stones (single number). If your UI
 * presents two fields ("stones" + "pounds"), combine them yourself
 * before calling: `displayToKg(stones + pounds/14, 'stones')`.
 */
export function displayToKg(weightDisplay: number, units: UnitSystem | undefined): number {
  if (units === 'imperial') return weightDisplay * LBS_TO_KG;
  if (units === 'stones') return weightDisplay * KG_PER_STONE;
  return weightDisplay;
}

/** The fields of a plan exercise that say its weight. */
export interface PlannedLoad {
  estimated_weight_kg?: number | null;
  prescribed_load?: number | string | null;
  load_unit?: string | null;
}

/**
 * A plan exercise's prescribed weight in KILOGRAMS, or undefined when it has none.
 *
 * The server sends it already in kilograms as `estimated_weight_kg`
 * (services/exercise_load_projection.py), and that wins. Without it, a NUMERIC
 * `prescribed_load` is read in its `load_unit`: "lb" / "lbs" are pounds, anything else
 * kilograms — the server's own rule (services/canonical_workout.plan_load_kg), divided by
 * the same factor so a pounds box shows the plan's number. "BW" is the bodyweight tag,
 * never a load. Until 2026-10-22 the phone read every `prescribed_load` as kilograms: a
 * 20 lb goblet squat was a 44 lb seed.
 */
export function plannedLoadKg(ex: PlannedLoad | null | undefined): number | undefined {
  if (!ex) return undefined;
  if (typeof ex.estimated_weight_kg === 'number') return ex.estimated_weight_kg;
  const load = ex.prescribed_load;
  if (typeof load !== 'number' || !(load > 0)) return undefined;
  const unit = String(ex.load_unit ?? '').toLowerCase();
  return unit === 'lb' || unit === 'lbs' ? load / KG_TO_LBS : load;
}

/**
 * The single canonical formatter for displaying a weight in the UI.
 *
 * Returns:
 *   - `"60 kg"` / `"132 lbs"` for metric/imperial inputs
 *   - `"12 st 7 lb"` for stones inputs (British composite convention).
 *     Edge cases: round-up to a full stone yields `"X st"` (no lb
 *     suffix); zero pound remainder also yields `"X st"`.
 *   - `null` for null / undefined / NaN inputs (callers decide whether
 *     to render a placeholder like "—" or omit the chip entirely)
 *
 * Use this anywhere you would otherwise write `${w}kg` or `${w} kg` in
 * a JSX text node or template literal. NEVER hardcode the unit literal
 * — the gate test will fail.
 *
 * For React components prefer `useFormatWeight()` which closes over the
 * current units, eliminating the per-call `units` argument.
 */
export function formatWeight(
  weightKg: number | null | undefined,
  units: UnitSystem | undefined,
): string | null {
  if (weightKg == null || !Number.isFinite(weightKg)) return null;

  if (units === 'stones') {
    // British composite format: "X st Y lb". Round pound remainder to
    // the nearest integer; if it rounds up to a full stone, promote.
    const totalLbs = weightKg * KG_TO_LBS;
    let stones = Math.floor(totalLbs / LBS_PER_STONE);
    let lbs = Math.round(totalLbs - stones * LBS_PER_STONE);
    if (lbs === LBS_PER_STONE) {
      stones += 1;
      lbs = 0;
    }
    if (lbs === 0) return `${stones} st`;
    return `${stones} st ${lbs} lb`;
  }

  return `${kgToDisplay(weightKg, units)} ${weightUnitLabel(units)}`;
}

/**
 * Resolve the unit a WEIGHT should be displayed in and accepted in.
 *
 * 🚨 THIS IS THE ONLY PLACE THE WEIGHT UNIT MAY BE DERIVED FROM THE PROFILE.
 * Every weight surface must go through `useWeightUnit()` (or this function).
 * Reading `profile.units` directly for a weight is a bug — the gate test
 * tests/gate/test_weight_unit_single_resolver.py blocks regressions.
 *
 * Why two fields exist, and why `units` alone is wrong for weight:
 *   - `weightUnit` is the GRANULAR weight preference. The backend resolves it
 *     from user_core_profile.weight_unit (seeded from the user's country at
 *     signup — both US and Canada get 'lbs') and serves it on
 *     GET /api/memory/profile behind FF_COMPLETION_UNIT_FROM_PROFILE.
 *   - `units` is the COARSE system flag. It also drives DISTANCE and
 *     TEMPERATURE, so the backend deliberately does NOT send it: forcing it
 *     imperial for Canada is the documented 2026-05-15 regression (°F in the
 *     weather strip, miles in coaching text). It is gate-pinned absent in
 *     tests/gate/test_profile_weight_unit_served.py::test_coarse_units_is_not_served.
 *
 * Because the server never sends `units`, ProfileContext falls back to
 * DEFAULT_UNITS = device-locale inference whose only imperial region is "US".
 * So a bare `profile.units` read resolves to 'metric' — i.e. KG — for every
 * non-US-locale device, including the 244 of 251 profiles whose stored
 * weight_unit is 'lbs'. That mislabels every displayed weight AND, on WRITE
 * paths, makes displayToKg() the identity so a typed pound value is stored as
 * kilograms (2.2x inflation in exercise_set_log).
 *
 * The `units` fallback is retained for the pre-fix window only: an older client
 * or a flag-off backend leaves `weightUnit` undefined, and falling back to
 * `units` reproduces exactly the previous behaviour rather than a third variant.
 */
export function resolveWeightUnit(
  profile:
    | { weightUnit?: UnitSystem | null; units?: UnitSystem | null }
    | null
    | undefined,
): UnitSystem {
  return (profile?.weightUnit ?? profile?.units ?? 'metric') as UnitSystem;
}

/**
 * A jest mock of ProfileContext may export only `useProfile` — then degrade to
 * "no server fields" (the pre-lane output) rather than crash. The choice is made
 * ONCE, at module load, so every render calls the same hook unconditionally
 * (react-hooks/rules-of-hooks: a hook must never sit behind a `typeof` test at
 * its call site).
 */
const useOptionalProfileIfExported: () => ReturnType<typeof useOptionalProfile> | undefined =
  typeof useOptionalProfile === 'function' ? useOptionalProfile : () => undefined;

/**
 * UNITS-U2 (owner ruling #42): the athlete's distance / temperature / height
 * preferences — server fields first, the device-region guess only for a field
 * the server did not send (flag off == the pre-lane behaviour). Weight keeps
 * its own resolver (`useWeightUnit`).
 */
export function useUnitPreferences(): ResolvedUnitPreferences {
  const ctx = useOptionalProfileIfExported();
  const profile = ctx?.profile ?? null;
  return useMemo(() => resolveUnitPreferences(profile), [profile]);
}

/** FIX-UNITS-1 item 7: whether an open-water swim reads in metres (served). */
export function useOpenWaterSwimMetric(): boolean {
  const ctx = useOptionalProfileIfExported();
  return openWaterSwimMetric(ctx?.profile as { openWaterSwimMetric?: boolean } | null | undefined);
}

/**
 * UNITS-U5A: the unit THIS athlete reads a sport's rate in — /mi or mph for a
 * miles athlete, knots for the sailing family, /100yd in a yard pool — when the
 * server opted them in; the sport's own unit otherwise (pre-lane, exactly).
 * Keep the SPORT unit (resolveRateUnit) for logic (zones, cadence rpm/spm) and
 * use this one only to FORMAT and LABEL.
 */
export function useDisplayRateUnit(
  sportUnit: RateUnit,
  labels: Array<string | null | undefined> = [],
  poolLengthM?: unknown,
): RateUnit {
  const prefs = useUnitPreferences();
  return resolveDisplayRateUnit(sportUnit, {
    distanceUnit: prefs.distance,
    poolLengthM,
    labels,
    enabled: prefs.ratesFollowPreference,
  });
}

/**
 * React hook form of `resolveWeightUnit` — the unit every weight surface
 * should render and accept in. Use this instead of `useProfile().profile.units`.
 */
export function useWeightUnit(): UnitSystem {
  const { profile } = useProfile();
  return resolveWeightUnit(profile);
}

/**
 * React hook that returns a memoized weight formatter closed over the
 * user's current units preference. Re-binds only when units change.
 *
 * Usage:
 *   const formatWeight = useFormatWeight();
 *   <Text>Squat @ {formatWeight(60)}</Text>   // "Squat @ 60 kg" / "@ 132 lbs" / "@ 9 st 6 lb"
 *
 * Returns the same shape as `formatWeight(kg, units)` — `null` for
 * null/undefined/NaN inputs.
 *
 * Resolves via `useWeightUnit()`, so it honours the granular `weightUnit`
 * (which CAN be 'stones') and only falls back to the binary `units` flag
 * when the server has not sent one.
 */
export function useFormatWeight(): (weightKg: number | null | undefined) => string | null {
  const units = useWeightUnit();
  return useMemo(
    () => (weightKg: number | null | undefined) => formatWeight(weightKg, units),
    [units],
  );
}
